import { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { BehaviorSubject, ReplaySubject, interval, Observable, of } from "rxjs";
import { fromFetch } from "rxjs/fetch";

import {
  distinctUntilChanged,
  catchError,
  map,
  switchMap,
  take,
  tap,
  shareReplay,
} from "rxjs/operators";

export const useObservable = <T>(
  val: T,
  type: "behavior" | "replay" = "behavior",
): Observable<T> => {
  const subject = useMemo(() => {
    if (type === "behavior") {
      return new BehaviorSubject<T>(val);
    } else {
      return new ReplaySubject<T>(1);
    }
  }, [type]);

  const initial = useRef(true);

  useEffect(() => {
    if (!initial.current) {
      subject.next(val);
    } else {
      initial.current = false;
    }
  }, [val, subject]);

  return subject;
};

export const useObsRef = <T>(obs?: Observable<T>) => {
  const ref = useRef<T>();

  useEffect(() => {
    const sub = obs?.subscribe((val) => {
      ref.current = val;
    });

    return () => {
      sub?.unsubscribe();
    };
  }, [obs]);

  return ref;
};

const getObsVal = <T>(obs?: Observable<T>): T | undefined => {
  let val: undefined | T;

  const sub = obs?.subscribe((v) => {
    val = v;
  });

  sub?.unsubscribe();
  return val;
};

export const useObsState = <T>(
  obs?: Observable<T>,
  init?: boolean,
): T | undefined => {
  const [value, setValue] = useState<T | undefined>(
    init ? getObsVal(obs) : undefined,
  );

  useEffect(() => {
    if (!obs) {
      setValue(undefined);
    }

    const sub = obs?.subscribe((val) => {
      setValue(() => val);
    });

    return () => {
      sub?.unsubscribe();
    };
  }, [obs]);

  return value;
};

type Validator<T> = (raw: any) => raw is T;
type SubjectType = "behavior" | "replay" | undefined;

export const useSubject = <T = undefined, ST extends SubjectType = SubjectType>(
  initial?: T,
  type?: ST,
) => {
  // @ts-ignore
  const ref = useRef<T>(initial);

  const subject = useMemo<
    ST extends "replay" ? ReplaySubject<T> : BehaviorSubject<T>
  >(
    () =>
      // @ts-expect-error
      type === "replay"
        ? new ReplaySubject<T>(1)
        : new BehaviorSubject(ref.current),
    [type],
  );

  useEffect(() => {
    if (ref.current && type === "replay") {
      subject.next(ref.current);
    }
  }, [type, subject]);

  return subject;
};

export const useState$ = <T = undefined>(initValue?: T) => {
  const state$ = useSubject(initValue);

  const setState = useCallback(
    (value: T) => {
      state$.next(value);
    },
    [state$],
  );

  return useMemo(() => [state$, setState] as const, [state$, setState]);
};

interface RequestInit {
  body?: any;
  credentials?: any;
  headers?: any;
  integrity?: any;
  keepalive?: any;
  method?: any;
  mode?: any;
  referrer?: any;
  window?: any;
  signal?: any;
}

type FetchOptions<T> = {
  validate: Validator<T>;
  onAfter?: () => void;
  onBefore?: () => void;
  onError?: (e: unknown) => void | boolean;
  retryTimeout?: number;
} & Omit<RequestInit, "body">;

export const useFetchCreator = <T>(params: FetchOptions<T>) => {
  const pars = useRef(params);
  const query$ = useSubject<string | [string, string] | undefined>();

  const fetch$ = useMemo(
    () =>
      query$.pipe(
        distinctUntilChanged((prev, next) => {
          if (prev === next) {
            return true;
          }

          const pA = Array.isArray(prev) ? prev : [prev, undefined];
          const nA = Array.isArray(next) ? next : [next, undefined];

          return pA.every((val, i) =>
            val && nA[i] ? val === nA[i] : !val && !nA[i],
          );
        }),

        tap(() => {
          pars.current.onBefore?.();
        }),

        switchMap((q) => {
          const [query, body] = Array.isArray(q)
            ? q
            : [q ?? undefined, undefined];

          if (!query) {
            return of(undefined);
          }

          const { validate, onError, retryTimeout, ...rest } = pars.current;

          return fromFetch(query, { body, ...rest }).pipe(
            switchMap((response) => {
              if (!response.ok) {
                throw new Error(`status ${response.status}`);
              }

              return response.text();
            }),

            distinctUntilChanged(),

            map((raw) => {
              const json = JSON.parse(raw);

              if (!validate(json)) {
                throw new Error(
                  `unexpected result, got ${JSON.stringify(json)}`,
                );
              }

              return json;
            }),

            catchError((err, caught) =>
              !onError?.(err)
                ? of(undefined)
                : interval(retryTimeout).pipe(
                    take(1),
                    switchMap(() => caught),
                  ),
            ),

            tap(() => {
              pars.current.onAfter?.();
            }),
          );
        }),
        shareReplay(1),
      ),
    [query$],
  );

  return useCallback(
    (query?: string, body?: string) => {
      if (!body) {
        query$.next(query);
      } else {
        query$.next([query!, body]);
      }
      return fetch$;
    },
    [query$, fetch$],
  );
};
